import React, {useRef, useEffect, useState} from "react";
import Plyr, {APITypes, PlyrInstance} from "plyr-react";
import {Button, Modal} from 'antd';
import {FormattedMessage, useIntl} from 'react-intl';
import "plyr-react/plyr.css";
import {ExclamationCircleOutlined} from "@ant-design/icons";
import {config} from "../../config/config";

const PlyrComponent = ({video, endVideoSession, endLoading, finish}: any) => {
    const ref = useRef<APITypes>(null);
    const intl = useIntl();
    const {confirm} = Modal;
    const [key, setKey] = useState(0)
    const [display, setDisplay] = useState(false)

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (video.settings?.pause_on_blur && document.visibilityState === 'hidden') {
                if (ref.current?.plyr) {
                    (ref.current.plyr as PlyrInstance).pause();
                }
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    })

    useEffect(() => {
        const player = ref.current?.plyr as PlyrInstance;
        if (!player?.on) {
            return;
        }
        const handleEnded = () => endVideoSession(true);
        player.on('ended', handleEnded);

        return () => player.off('ended', handleEnded);
    })

    const rewind = (sec: number) => {
        (ref.current?.plyr as PlyrInstance)?.rewind(sec);
    };

    const forward = (sec: number) => {
        (ref.current?.plyr as PlyrInstance)?.forward(sec);
    };

    const pause = () => {
        (ref.current?.plyr as PlyrInstance)?.togglePlay();
    };

    const pauseVideo = () => {
        (ref.current?.plyr as PlyrInstance)?.pause();
    };

    const plyrVideo =
        video.videoId && video.videoProvider ? (
            <Plyr
                ref={ref}
                source={{
                    type: "video",
                    sources: [
                        {
                            src: video.videoId,
                            provider: video.videoProvider
                        }
                    ]
                }}
                options={
                    {
                        disableContextMenu: true,
                        controls: video.settings?.video_progress ? undefined : ['play-large', 'play', 'progress', 'mute', 'volume', 'fullscreen'],
                        autoplay: true,

                    }
                }
            />
        ) : null;

    const handleFinish = () => {
        const player = ref.current?.plyr as PlyrInstance;
        const isWatched = Math.floor(player?.currentTime || 0) >= Math.floor(player?.duration || 0) - 3;
    
        if (video.settings.view_all_to_complete && !isWatched) {
            confirm({
                title: intl.formatMessage({id: 'general.attention'}),
                content: intl.formatMessage({id: 'student.video_warning'}),
                icon: <ExclamationCircleOutlined/>,
                okText: intl.formatMessage({id: 'general.yes'}),
                cancelText: intl.formatMessage({id: 'general.cancel'}),
                onOk() {
                    endVideoSession(false)
                },
                onCancel() {
                    pause()
                }
            });
        } else {
            endVideoSession(true);
        }
    }

    return (
        <div key={key}>
            <Button className={'rerender-button '+ (display ? 'hidden': '')} onClick={() => {setKey(key+1); setDisplay(true)}}>
                <FormattedMessage id='general.reload'/>
            </Button>
            {plyrVideo}
            <div className='buttons-container'>
                <div className='button-wrapper'>
                    <Button onClick={() => rewind(5)}>
                        <i className="fa-solid fa-backward"></i>
                        <FormattedMessage id='courses.rewind_video'/>
                    </Button>
                    <Button onClick={() => pause()}>
                        <i className="fa-solid fa-play-pause"></i>
                    </Button>
                    {config.api.environment === 'development' &&
                        <Button onClick={() => forward(5)}>
                            <i className="fa-solid fa-forward"></i>
                        </Button>
                    }
                </div>
                <Button loading={endLoading} onClick={() => handleFinish()}>
                    <FormattedMessage id='courses.finish'/>
                </Button>
            </div>
        </div>
    );
};

export default PlyrComponent;
